import { Box, LinearProgress, Typography } from "@mui/material"
import { LinkOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import { type FC, useState } from "react"

import { AssociatedProductsTable } from "./AssociatedProductsTable"
import { AssociateEquivalentDialog } from "./EquivalentProducts/AssociateEquivalentDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { EquivalencyGroup } from "@/graphql/codegen/graphql"
import useEquivalencyGroupProducts from "@/screens/ManageProducts/components/EquivalentProductGroups/hooks/useEquivalencyGroupProducts"
import { useRemoveProductFromEquivalencyGroup } from "@/screens/ManageProducts/components/EquivalentProductGroups/hooks/useRemoveProductFromEquivalencyGroup"

interface EquivalentProductGroupExpandedRowProps {
  row: Row<EquivalencyGroup>
}

const EquivalentProductGroupExpandedRow: FC<EquivalentProductGroupExpandedRowProps> = ({ row }) => {
  const group = row.original

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // Fetch associated products when row expands
  const { products, competitorProducts, fetching, refetchProductsList } = useEquivalencyGroupProducts({
    equivalencyGroupId: group.equivalencyGroupId,
  })

  const { removeProduct } = useRemoveProductFromEquivalencyGroup({
    equivalencyGroupId: group.equivalencyGroupId,
    onSuccess: refetchProductsList,
  })

  const hasNoProducts = !fetching && !products.length && !competitorProducts.length

  // Get associated product IDs from fetched data
  const associatedProductIds = products.map((p) => p.productId)
  const associatedCompetitorIds = competitorProducts.map((cp) => cp.competitorProductId)

  const handleAssociateEquivalent = () => {
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  const handleSuccess = () => {
    refetchProductsList()
  }

  return (
    <Box className='flex flex-col gap-4 px-6 pb-6 pt-4'>
      {group.notes && (
        <Box className='my-2 rounded border border-gray-200 bg-white px-4 py-3'>
          <div className='text-sm text-gray-700'>{group.notes}</div>
        </Box>
      )}

      {fetching && (
        <Box className='py-4'>
          <LinearProgress />
        </Box>
      )}

      {!fetching && hasNoProducts && (
        <InformationMissingAlert
          icon={<WarningOutlined color='primary' />}
          title='Begin by adding product equivalents to this group'
          action={
            <Button
              color='primary'
              size='small'
              appearance='outlined'
              startIcon={<LinkOutlined />}
              onClick={handleAssociateEquivalent}
              data-testid='associate-equivalent-button'
            >
              Associate Equivalent
            </Button>
          }
        />
      )}

      {!fetching && !hasNoProducts && (
        <>
          <Box className='flex items-center justify-between'>
            <Typography variant='h6' className='text-lg font-normal text-gray-900'>
              Equivalent Products
            </Typography>
            <Button
              color='primary'
              size='small'
              appearance='outlined'
              startIcon={<LinkOutlined />}
              onClick={handleAssociateEquivalent}
              data-testid='associate-equivalent-button'
            >
              Associate Equivalent
            </Button>
          </Box>
          <AssociatedProductsTable
            akrochemProducts={products}
            competitorProducts={competitorProducts}
            onRemove={removeProduct}
          />
        </>
      )}

      {isDialogOpen && (
        <AssociateEquivalentDialog
          equivalencyGroupId={group.equivalencyGroupId}
          onClose={handleCloseDialog}
          onSuccess={handleSuccess}
          associatedProductIds={associatedProductIds}
          associatedCompetitorIds={associatedCompetitorIds}
        />
      )}
    </Box>
  )
}

export default EquivalentProductGroupExpandedRow
